import * as React from "react";
import { CalendarClock, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

/** Site calendar editor: opening hours per weekday + closures.
 *
 * Read-only for anyone without `sites.manage`. When editing:
 *   - Toggle Open/Closed per weekday.
 *   - Choose opens_at / closes_at (24-hour HH:MM).
 *   - Add a closure (single date + optional reason).
 *   - Remove a closure inline.
 */
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DEFAULT_DAY = { open: true, opens_at: "09:00", closes_at: "17:00" };

export default function SiteCalendarPanel({ companyId, siteId, canEdit, testidPrefix = "site-calendar" }) {
  const [days, setDays] = React.useState(() => DOW.map((_, i) => ({ weekday: i, ...DEFAULT_DAY })));
  const [closures, setClosures] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [closureDraft, setClosureDraft] = React.useState({ date: "", reason: "" });

  const load = React.useCallback(async () => {
    const { data } = await api.get(`/companies/${companyId}/sites/${siteId}/calendar`);
    const byWd = Object.fromEntries((data.days || []).map((d) => [d.weekday, d]));
    setDays(DOW.map((_, i) => ({ weekday: i, ...(byWd[i] || DEFAULT_DAY) })));
    setClosures(data.closures || []);
  }, [companyId, siteId]);
  React.useEffect(() => { load(); }, [load]);

  function updateDay(i, patch) {
    if (!canEdit) return;
    setDays((cur) => cur.map((d) => (d.weekday === i ? { ...d, ...patch } : d)));
  }

  async function saveHours() {
    if (busy) return;
    // Validate before hitting the network
    for (const d of days) {
      if (d.open && d.opens_at >= d.closes_at) {
        toast.error(`${DOW[d.weekday]}: opens_at must be earlier than closes_at`);
        return;
      }
    }
    setBusy(true);
    try {
      await api.put(`/companies/${companyId}/sites/${siteId}/calendar`, { days });
      toast.success("Calendar saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function addClosure() {
    if (!closureDraft.date || busy) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/sites/${siteId}/closures`, closureDraft);
      setClosureDraft({ date: "", reason: "" });
      toast.success("Closure added");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function removeClosure(id) {
    try {
      await api.delete(`/companies/${companyId}/sites/${siteId}/closures/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  return (
    <div className="p-5 space-y-5" data-testid={`${testidPrefix}-panel`}>
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <CalendarClock size={13} className="text-brand-muted" />
          <div className="micro-label">Opening hours</div>
        </div>
        <div className="space-y-1">
          {days.map((d) => (
            <div key={d.weekday}
                 className="grid grid-cols-[70px_1fr_1fr_60px] items-center gap-2 py-1.5 border-b border-brand-rule/60 last:border-b-0"
                 data-testid={`${testidPrefix}-day-${d.weekday}`}>
              <div className="text-[12.5px] font-medium text-brand-ink">{DOW[d.weekday]}</div>
              <input type="time" className="field-input !h-8 mono-num"
                     value={d.opens_at} onChange={(e) => updateDay(d.weekday, { opens_at: e.target.value })}
                     disabled={!canEdit || !d.open}
                     data-testid={`${testidPrefix}-opens-${d.weekday}`} />
              <input type="time" className="field-input !h-8 mono-num"
                     value={d.closes_at} onChange={(e) => updateDay(d.weekday, { closes_at: e.target.value })}
                     disabled={!canEdit || !d.open}
                     data-testid={`${testidPrefix}-closes-${d.weekday}`} />
              <label className="text-[11px] text-brand-muted flex items-center gap-1 cursor-pointer">
                <input type="checkbox" className="accent-brand-violet"
                       checked={!!d.open} onChange={(e) => updateDay(d.weekday, { open: e.target.checked })}
                       disabled={!canEdit}
                       data-testid={`${testidPrefix}-open-${d.weekday}`} />
                open
              </label>
            </div>
          ))}
        </div>
        {canEdit && (
          <button className="btn-violet mt-3" onClick={saveHours} disabled={busy}
                  data-testid={`${testidPrefix}-save`}>
            <Save size={13} /> {busy ? "…" : "Save hours"}
          </button>
        )}
      </div>

      <div>
        <div className="micro-label mb-2">Closures</div>
        <div className="space-y-1">
          {closures.map((c) => (
            <div key={c.id}
                 className="flex items-center gap-2 py-1.5 border-b border-brand-rule/60 last:border-b-0"
                 data-testid={`${testidPrefix}-closure-${c.id}`}>
              <span className="mono-num text-[12px] text-brand-ink">{c.date}</span>
              <span className="text-[12px] text-brand-muted flex-1 truncate">{c.reason || "—"}</span>
              {canEdit && (
                <button className="btn-ghost !h-6 !px-1 !text-brand-coral"
                        onClick={() => removeClosure(c.id)}
                        data-testid={`${testidPrefix}-closure-rm-${c.id}`}>
                  <X size={13} />
                </button>
              )}
            </div>
          ))}
          {closures.length === 0 && (
            <div className="text-[12px] text-brand-muted py-3">
              No closures. Add a date below to block the availability engine.
            </div>
          )}
        </div>
        {canEdit && (
          <div className="mt-3 grid grid-cols-[140px_1fr_auto] gap-2">
            <input type="date" className="field-input !h-8 mono-num"
                   value={closureDraft.date}
                   onChange={(e) => setClosureDraft((s) => ({ ...s, date: e.target.value }))}
                   data-testid={`${testidPrefix}-closure-date`} />
            <input className="field-input !h-8" placeholder="Reason (optional)"
                   value={closureDraft.reason}
                   onChange={(e) => setClosureDraft((s) => ({ ...s, reason: e.target.value }))}
                   data-testid={`${testidPrefix}-closure-reason`} />
            <button className="btn-outline !h-8" onClick={addClosure} disabled={!closureDraft.date || busy}
                    data-testid={`${testidPrefix}-closure-add`}>
              Add
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
